// src/contexts/AuthContext.tsx
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  isSystemAdmin: () => boolean;
  isTenantUser: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch {
          // Corrupted data, clear it
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          setToken(null);
        }
      }
    }
    setLoading(false);
  }, [token]);

  const login = async (username: string, password: string) => {
    try {
      const response = await authService.login({ username, password });
      const data = response.data;

      const newUser: User = {
        username: data.username,
        displayName: data.displayName,
        role: data.role,
        tenantId: data.tenantId,
        tenantName: data.tenantName
      };

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(newUser));
      setToken(data.token);
      setUser(newUser);

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Clean up old key if present
    localStorage.removeItem('username');
    setToken(null);
    setUser(null);
  };

  const isSystemAdmin = () => user?.role === 'SystemAdmin';
  const isTenantUser = () => user?.role === 'TenantAdmin' || user?.role === 'TenantUser';

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isSystemAdmin,
    isTenantUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
